import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, ArrowLeft } from "lucide-react";
import {API_URL,FAST_API_URL} from "../api"

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "ar", label: "العربية" }
];

const FooterChatbot = () => {
  const [open, setOpen] = useState(false);
  const [language, setLanguage] = useState("");
  const [faqs, setFaqs] = useState([]);
  const [category, setCategory] = useState(null);
  const [messages, setMessages] = useState([
    { from: "bot", text: "👋 Please select your language to begin." }
  ]);

  const chatEndRef = useRef(null);

  /* 🔄 Reset chatbot completely */
  const resetChatbot = () => {
    setLanguage("");
    setFaqs([]);
    setCategory(null);
    setMessages([
      { from: "bot", text: "👋 Please select your language to begin." }
    ]);
  };

  /* 📥 Load FAQs */
  const loadFaqs = async (lang) => {
    try {
      const res = await axios.post(
       `${API_URL}/qcares/faqs`,
        { lang }
      );
      setFaqs(res.data.categories || []);
    } catch {
      setMessages([
        { from: "bot", text: "⚠️ Unable to load questions." }
      ]);
    }
  };

  /* 🤖 Ask Bot */
  const askBot = async (question) => {
    setMessages((prev) => [...prev, { from: "user", text: question }]);

    try {
      const res = await axios.post(
       `${API_URL}/qcares_chatbot_query`,
        { question, lang: language }
      );

      setMessages((prev) => [
        ...prev,
        { from: "bot", text: res.data.answer || "—" }
      ]);

      // after answer → show categories again
      setCategory(null);
    } catch {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: "⚠️ Server error. Try again later." }
      ]);
      setCategory(null);
    }
  };

  /* ⬇️ Auto scroll */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-br from-teal-500 to-emerald-500 text-white p-4 rounded-full shadow-2xl z-50"
      >
        <MessageCircle size={26} />
      </button>

      {open && (
        <div className="fixed bottom-6 right-6 w-[95%] sm:w-[380px] h-[85vh] bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl z-50 flex flex-col">

          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-emerald-500 p-4 text-white flex justify-between items-center">
            <h2 className="font-semibold">AICA Assistant</h2>
            <X
              className="cursor-pointer"
              onClick={() => {
                resetChatbot();
                setOpen(false);
              }}
            />
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-gray-100">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${
                  msg.from === "user"
                    ? "ml-auto bg-teal-600 text-white"
                    : "bg-white shadow"
                }`}
              >
                {msg.text}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Controls */}
          <div className="p-4 bg-white border-t space-y-3">

            {/* 🌐 Language Selection */}
            {!language && (
              <div className="grid grid-cols-2 gap-2">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      loadFaqs(lang.code);
                    }}
                    className="px-4 py-3 bg-gray-100 hover:bg-teal-50 rounded-xl text-sm"
                  >
                    🌐 {lang.label}
                  </button>
                ))}
              </div>
            )}

            {/* ⬅ Back to Categories */}
            {language && category && (
              <button
                onClick={() => setCategory(null)}
                className="flex items-center gap-2 text-sm text-teal-600 hover:underline"
              >
                <ArrowLeft size={16} /> Back to Categories
              </button>
            )}

            {/* 📂 Categories */}
            {language && !category && (
              <div className="grid grid-cols-2 gap-2">
                {faqs.map((cat, i) => (
                  <button
                    key={i}
                    onClick={() => setCategory(cat)}
                    className="px-4 py-3 bg-gray-100 hover:bg-teal-50 rounded-xl text-sm"
                  >
                    📂 {cat.name}
                  </button>
                ))}
              </div>
            )}

            {/* ❓ Questions */}
            {category && (
              <div className="space-y-2">
                {category.questions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => askBot(q)}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-teal-50 rounded-xl text-sm"
                  >
                    ❓ {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FooterChatbot;
